import { useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { EyeSlashIcon } from '@heroicons/react/24/solid';

import { Button as ButtonV2, Heading, Modal, ModalContent } from '@restworld/ui-ds-base';
import { Alert } from '@restworld/ui-components';

import { formatCoveredUntil, isCoveredUntilActionable } from '../utils/covered-until';

interface PutOfflineJobPositionConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  isSubscriptionFeedbacksSubmitted?: boolean;
  ppuPaidUntil?: string | null;
  ppuOfflineScheduledAt?: string | null;
  onConfirmScheduled?: () => void;
  isScheduling?: boolean;
  // True while the paid-until time is still being fetched: no choice is offered until it is known.
  isLoadingPaidUntil?: boolean;
}

export function PutOfflineJobPositionConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  isSubscriptionFeedbacksSubmitted = false,
  ppuPaidUntil,
  ppuOfflineScheduledAt,
  onConfirmScheduled,
  isScheduling,
  isLoadingPaidUntil = false,
}: PutOfflineJobPositionConfirmationModalProps) {
  const { t, i18n } = useTranslation('job-positions-page');
  const language = i18n?.language ?? 'it';

  const handleConfirm = useCallback(() => {
    onConfirm();
  }, [onConfirm]);

  const handleConfirmScheduled = useCallback(() => {
    onConfirmScheduled?.();
  }, [onConfirmScheduled]);

  const isBusy = !!isLoading || !!isScheduling;

  const scheduledAt = useMemo(
    () => (ppuOfflineScheduledAt ? formatCoveredUntil(ppuOfflineScheduledAt, t, language) : null),
    [ppuOfflineScheduledAt, t, language]
  );

  const paidUntil = useMemo(
    () =>
      !ppuOfflineScheduledAt && onConfirmScheduled && ppuPaidUntil && isCoveredUntilActionable(ppuPaidUntil)
        ? formatCoveredUntil(ppuPaidUntil, t, language)
        : null,
    [ppuOfflineScheduledAt, onConfirmScheduled, ppuPaidUntil, t, language]
  );

  const titleEl = (
    <Heading color="neutral" align="center" className="leading-[30px] px-4 sm:px-0">
      {t('put_offline_confirmation_title')}
    </Heading>
  );

  const keepOnlineButton = (
    <ButtonV2 variant="tertiary" fullwidth onClick={() => onClose()} disabled={isBusy}>
      {t('put_offline_confirmation_cancel_button')}
    </ButtonV2>
  );

  const putOfflineNowButton = (label: string) => (
    <ButtonV2
      onClick={handleConfirm}
      fullwidth
      loading={isLoading}
      StartIcon={EyeSlashIcon}
      variant="secondary-danger"
      disabled={isBusy}
    >
      {label}
    </ButtonV2>
  );

  let footerEl;
  if (isLoadingPaidUntil) {
    // Until the paid-until time arrives the modal cannot tell which choices apply: showing the
    // plain "put offline" button meanwhile let an employer go offline without seeing the
    // "when it expires" option.
    footerEl = (
      <div className="flex flex-col justify-center items-center gap-4 w-full">
        <ButtonV2 fullwidth loading disabled variant="secondary-danger" data-testid="put-offline-loading">
          {t('put_offline_confirmation_continue_button')}
        </ButtonV2>
        {keepOnlineButton}
      </div>
    );
  } else if (scheduledAt) {
    // Going offline is already scheduled: only "now" is left to choose.
    footerEl = (
      <div className="flex flex-col justify-center items-center gap-4 w-full">
        <p className="px-1 text-center text-sm leading-5 font-medium text-neutral-700">
          {t('put_offline_already_scheduled_info', scheduledAt)}
        </p>
        {putOfflineNowButton(t('put_offline_option_now_button'))}
        {keepOnlineButton}
      </div>
    );
  } else if (paidUntil) {
    // Paid period still running: offer going offline when it expires or right away.
    footerEl = (
      <div className="flex flex-col justify-center items-center gap-4 w-full">
        <p className="px-1 text-center text-sm leading-5 font-semibold text-neutral-700">
          {t('put_offline_paid_until_notice', paidUntil)}
        </p>
        <div className="flex flex-col items-center gap-1 w-full">
          <ButtonV2
            onClick={handleConfirmScheduled}
            fullwidth
            loading={isScheduling}
            variant="primary"
            disabled={isBusy}
          >
            {t('put_offline_option_scheduled_button', { time: paidUntil.time })}
          </ButtonV2>
          <p className="px-1 text-center text-xs leading-4 text-neutral-600">
            {t('put_offline_option_scheduled_hint')}
          </p>
        </div>
        <div className="flex flex-col items-center gap-1 w-full">
          {putOfflineNowButton(t('put_offline_option_now_button'))}
          <p className="px-1 text-center text-xs leading-4 text-neutral-600">
            {t('put_offline_option_now_hint', { time: paidUntil.time })}
          </p>
        </div>
        {keepOnlineButton}
      </div>
    );
  } else {
    footerEl = (
      <div className="flex flex-col justify-center items-center gap-4 w-full">
        {putOfflineNowButton(t('put_offline_confirmation_continue_button'))}
        {keepOnlineButton}
      </div>
    );
  }

  return (
    // master (clarify-candidate-feedback): candidate-access copy + feedback-deadline warning,
    // rendered in our ui-ds-base Modal/ModalContent + className styling.
    <Modal open={isOpen} onOpenChange={(v) => !v && onClose()}>
      <ModalContent className="w-full max-w-[640px] lg:max-w-screen-sm" title={titleEl} footer={footerEl}>
        <div className="flex flex-col gap-4 w-full mt-6 lg:pb-4">
          <p className="text-center text-sm leading-5.25 text-neutral-700 lg:text-base">
            {t('put_offline_confirmation_candidate_access')}
          </p>
          <Alert variant="warning" text={t('put_offline_confirmation_candidate_feedback_deadline')} />
        </div>
      </ModalContent>
    </Modal>
  );
}

export default PutOfflineJobPositionConfirmationModal;
